import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, FileWarning } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Button, Checkbox, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useGlobalConfig,
  useUpdateGlobalConfig,
  withConfig,
  useStaticFiles,
  useUploadStaticFile,
  type ErrorPageEntry,
} from '@/api/waap';
import { errMessage } from '../waap-util';
import { Labeled, SaveBar } from './settings-ui';
import styles from './HttpSettings.module.css';

// Static-file types (both confirmed live against the vendor). An error-page file
// is keyed to its status code by NAME — it must be uploaded as `err_<code>.html`,
// and the code is then linked by setting error_page[code].file_id to that file's
// id (a mismatched name 502s). Uploading a differently-named file, or not setting
// file_id, leaves the code showing no custom page.
const KILL_SWITCH_TYPE = 'kill_switch';
const ERR_PAGE_TYPE = 'err_page';
/** The vendor filename a custom page for `code` must use. */
const errPageFileName = (code: number) => `err_${code}.html`;

// Vendor portal base for the static-file HTML preview page (opens
// /security-setting/preview.html?fid=<file id> in a new tab). The preview lives
// on the vendor portal, so it requires an active portal session in the browser.
// The portal is now flashcone (was swiftfederation — that domain is dead).
// TODO(WAAP): move to env config if the portal host varies per environment.
const WAAP_PORTAL_BASE = 'https://waap2.flashcone.com';
const previewUrl = (fileId: number) =>
  `${WAAP_PORTAL_BASE}/security-setting/preview.html?fid=${fileId}`;

/**
 * HTTP Settings — maximum upload size (MB), response-body inspection size (KB),
 * the customised error-page table (per-code Replace toggle + HTML upload) and the
 * customised kill-switch page. Sizes + the Replace toggles round-trip through the
 * global-config; HTML files are uploaded via the shared static-files API.
 */
export function HttpSettings({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: record, isLoading } = useGlobalConfig(domainId);
  const { data: killSwitchFiles = [] } = useStaticFiles(domainId, KILL_SWITCH_TYPE);
  // Error-page files — refetched after an upload to resolve the new file's id
  // (the upload response is just {status:"ok"}), which is then linked to the code.
  const errPageFiles = useStaticFiles(domainId, ERR_PAGE_TYPE);

  const [fileSize, setFileSize] = useState('');
  const [bodySize, setBodySize] = useState('');
  const [errorPages, setErrorPages] = useState<ErrorPageEntry[]>([]);
  // Seed from the server only when the record actually changes (by `updated`),
  // so a background refetch can't clobber unsaved size edits / Replace toggles.
  const syncedUpdated = useRef<number | null>(null);
  useEffect(() => {
    if (!record || record.updated === syncedUpdated.current) return;
    syncedUpdated.current = record.updated;
    setFileSize(String(record.config.upload_max_size_num));
    setBodySize(String(record.config.inspection_setting.response_body_size));
    setErrorPages(record.config.error_page ?? []);
  }, [record]);

  const update = useUpdateGlobalConfig(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'HTTP Settings saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const upload = useUploadStaticFile(domainId, {
    onSuccess: (_d, vars) => show({ tone: 'success', title: `Uploaded ${vars.name}` }),
    onError: (e) => show({ tone: 'danger', title: 'Upload failed', description: errMessage(e) }),
  });

  const toggleReplace = (code: number, v: boolean) =>
    setErrorPages((rows) => rows.map((r) => (r.code === code ? { ...r, enable_replace: v } : r)));

  // One hidden file input, retargeted per Upload click (kill switch, or a specific
  // error-page code).
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingType = useRef<string>(ERR_PAGE_TYPE);
  const pendingCode = useRef<number>(0);
  const pickFor = (type: string, code = 0) => {
    pendingType.current = type;
    pendingCode.current = code;
    fileInput.current?.click();
  };
  const onFile = async (file: File) => {
    const content = await file.text();
    const type = pendingType.current;

    if (type === KILL_SWITCH_TYPE) {
      // Kill switch keeps a single file per type; replace in place by its id.
      upload.mutate({ content, name: file.name, type, id: killSwitchFiles[0]?.id ?? 0 });
      return;
    }

    // Error page: the vendor keys the file to the status code by NAME, so upload
    // it as `err_<code>.html` (replacing the code's existing file in place if any).
    // The upload response carries no id, so refetch the list, resolve the new id,
    // and link it into the code's row — the user then clicks Save to persist it
    // into global-config (error_page[code].file_id + custom_errpage_status).
    const code = pendingCode.current;
    const vendorName = errPageFileName(code);
    const existingId = Number(errorPages.find((r) => r.code === code)?.file_id) || 0;
    upload.mutate(
      { content, name: vendorName, type, id: existingId },
      {
        onSuccess: async () => {
          const { data: files = [] } = await errPageFiles.refetch();
          const f = files
            .filter((x) => x.name === vendorName)
            .sort((a, b) => b.id - a.id)[0];
          if (!f) return;
          // Only link file_id (+ turn Replace on). file_name / file_name_display are
          // vendor-canonical (err_<code>.html / <code>.html) and MUST be left as-is —
          // changing file_name_display to the user's filename 502s the config save.
          setErrorPages((rows) =>
            rows.map((r) => (r.code === code ? { ...r, file_id: f.id, enable_replace: true } : r)),
          );
          show({ tone: 'info', title: 'File attached — click Save to apply' });
        },
      },
    );
  };

  // Customised error-page table columns (per-code Replace toggle + HTML upload).
  const errorPageColumns = useMemo<ColumnDef<ErrorPageEntry>[]>(
    () => [
      { accessorKey: 'code', header: 'Error Code' },
      {
        header: 'Replace',
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.enable_replace}
            onCheckedChange={(v) => toggleReplace(row.original.code, !!v)}
            title="Use a custom page for this error code instead of the default."
          />
        ),
      },
      {
        header: 'File',
        // The vendor stores no user filename (it's always <code>.html), so a linked
        // custom page is signalled by file_id: show it as a clickable preview +
        // "custom" tag; otherwise it's the built-in default page.
        cell: ({ row }) => {
          const d = row.original;
          const label = d.file_name_display || d.file_name;
          return Number(d.file_id) > 0 ? (
            <a href={previewUrl(Number(d.file_id))} target="_blank" rel="noopener noreferrer" className={styles.link}>
              {label} (custom)
            </a>
          ) : (
            <span className={styles.muted}>{label} (default)</span>
          );
        },
      },
      {
        header: 'Upload',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Upload size={14} />}
            disabled={upload.isPending}
            onClick={() => pickFor(ERR_PAGE_TYPE, row.original.code)}
          >
            Upload
          </Button>
        ),
      },
    ],
    // toggleReplace/pickFor use functional setState + refs, so only the upload
    // pending state needs to re-derive the column cells.
    [upload.isPending],
  );

  const save = () => {
    if (!record) return;
    const mb = Number(fileSize);
    const kb = Number(bodySize);
    if (!Number.isFinite(mb) || mb <= 0 || !Number.isFinite(kb) || kb <= 0) {
      show({ tone: 'danger', title: 'File size and inspection size must be positive numbers' });
      return;
    }
    // The vendor requires custom_errpage_status (CSV of active codes) to stay in
    // sync with the error_page Replace flags — an inconsistency 500s. Derive it.
    const customErrpageStatus = errorPages
      .filter((p) => p.enable_replace)
      .map((p) => p.code)
      .join(',');
    update.mutate(
      withConfig(record, {
        upload_max_size_num: mb,
        inspection_setting: { ...record.config.inspection_setting, response_body_size: kb },
        error_page: errorPages,
        custom_errpage_status: customErrpageStatus,
      }),
    );
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <div className={styles.stack}>
      <input
        ref={fileInput}
        type="file"
        accept=".html,.htm,text/html"
        className={styles.hiddenInput}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />

      <Section
        title="Maximum File Size"
        description="Configure the maximum file size (in MB) to restrict the size of files being uploaded to the protected websites."
        style={{ marginBottom: 0 }}
      >
        <Labeled label="File Size" required hint="Requests uploading a file larger than this limit will be rejected.">
          <div className={styles.inputRow}>
            <Input
              type="number"
              value={fileSize}
              onChange={(e) => setFileSize(e.target.value)}
              className={styles.sizeInput}
            />
            <span className={styles.unit}>MB</span>
          </div>
        </Labeled>
      </Section>

      <Section
        title="Response Body Inspection Size"
        description="Configure the maximum response body size (KB) to maintain system performance and security."
        style={{ marginBottom: 0 }}
      >
        <Labeled label="Size" required hint="Response bodies larger than this size will not be inspected for content filtering.">
          <div className={styles.inputRow}>
            <Input
              type="number"
              value={bodySize}
              onChange={(e) => setBodySize(e.target.value)}
              className={styles.sizeInput}
            />
            <span className={styles.unit}>KB</span>
          </div>
        </Labeled>
      </Section>

      <Section
        title="Customised Error Page"
        description="Replace the default error pages with selected pages or upload a new HTML page. Note: the HTML page you upload must be UTF-8 encoded."
        style={{ marginBottom: 0 }}
      >
        <DataTable
          columns={errorPageColumns}
          data={errorPages}
          emptyState={
            <EmptyState
              size="sm"
              icon={<FileWarning size={20} />}
              title="No error pages"
              description="No customisable error codes are available for this account."
            />
          }
        />
      </Section>

      <Section
        title="Customised Kill Switch Page"
        description="When enabled, the system will intercept all requests and return a pre-configured HTML page as the response."
        style={{ marginBottom: 0 }}
      >
        <div className={styles.killRow}>
          <Labeled label="Kill Switch HTML Page">
            {killSwitchFiles[0] ? (
              <a
                href={previewUrl(killSwitchFiles[0].id)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {killSwitchFiles[0].name}
              </a>
            ) : (
              <span className={styles.muted}>No file uploaded</span>
            )}
          </Labeled>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Upload size={14} />}
            disabled={upload.isPending}
            onClick={() => pickFor(KILL_SWITCH_TYPE)}
          >
            Upload
          </Button>
        </div>
      </Section>

      <SaveBar onSave={save} saving={update.isPending} disabled={!record} />
    </div>
  );
}
